<!--
*  TTTech nerve-management-system-frontend
*  Copyright(c) 2026. TTTech Industrial Automation AG.
*
*  ALL RIGHTS RESERVED.
*
*  Usage of this software, including source code, netlists, documentation,
*  is subject to restrictions and conditions of the applicable license
*  agreement with TTTech Industrial Automation AG or its affiliates.
*
*  All trademarks used are the property of their respective owners.
*
*  TTTech Industrial Automation AG and its affiliates do not assume any liability
*  arising out of the application or use of any product described or shown
*  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
*  make changes, at any time, in order to improve reliability, function or
*  design.
*
*  Contact Information:
*  support@tttech-industrial.com
*
*  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
*
* -->
<template>
  <v-navigation-drawer
    id="iiotAppDrawer"
    ref="drawerRef"
    :class="{ 'hover-overlay-open': isHoverOverlayDrawer }"
    :model-value="drawerModel"
    :permanent="isDrawerPermanent"
    :temporary="isDrawerTemporary"
    :scrim="isDrawerOverlayMode"
    :rail="isRail"
    :rail-width="56"
    :width="240"
    app
    @update:model-value="handleDrawerModelUpdate"
    @mouseenter="handleDrawerMouseEnter"
    @mouseleave="handleDrawerMouseLeave"
    @focusin="handleDrawerFocusIn"
    @focusout="handleDrawerFocusOut"
  >
    <div id="iiotDrawerNavigationList" class="side">
      <v-list v-model:opened="openedGroups" nav class="pa-0">
        <div v-for="(link, i) in links" :key="i">
          <v-list-item
            v-if="!link.subLinks && link.isVisible"
            :id="generateId(link.to)"
            :active="isDrawerItemActive(link, i)"
            class="drawer-item"
            @click="handleDrawerItemClick(link)"
          >
            <template #prepend>
              <div class="icon-container" :class="{ 'drawer-item-padding': !isDrawerExpanded }">
                <v-icon v-if="link.icon" size="22">{{ link.icon }}</v-icon>
                <v-img
                  v-else
                  :src="`/img/${isDrawerItemActive(link) ? link.srcSelected : link.src}`"
                  width="22"
                  height="22"
                  contain
                />
              </div>
            </template>
            <v-list-item-title v-if="isDrawerExpanded" class="drawer-title">{{ link.text }}</v-list-item-title>
          </v-list-item>

          <v-list-group
            v-else-if="link.subLinks && link.isVisible"
            :id="generateId(link.name)"
            :value="getGroupKey(link, i)"
          >
            <template #activator="{ props, isOpen }">
              <v-list-item
                :id="generateId(link.name)"
                v-bind="props"
                :active="isDrawerItemActive(link, i)"
                class="drawer-item"
                @click.stop="handleDrawerItemClick(link)"
              >
                <template #prepend>
                  <div class="icon-container" :class="{ 'drawer-item-padding': !isDrawerExpanded }">
                    <v-icon v-if="link.icon" size="22">{{ link.icon }}</v-icon>
                    <v-img
                      v-else
                      :src="`/img/${isDrawerItemActive(link) ? link.srcSelected : link.src}`"
                      width="22"
                      height="22"
                      contain
                    />
                  </div>
                </template>

                <v-list-item-title v-if="isDrawerExpanded" class="drawer-title">{{ link.text }}</v-list-item-title>

                <template #append>
                  <v-btn
                    v-if="isDrawerExpanded && (link.isExistsSubElement ?? true)"
                    :icon="isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                    variant="text"
                    size="small"
                    class="group-arrow-btn"
                    @click.stop="props.onClick"
                  ></v-btn>
                </template>
              </v-list-item>
            </template>

            <v-list-item
              v-for="sublink in link.subLinks"
              v-show="isDrawerExpanded && sublink?.isVisible"
              :id="generateId(sublink.to)"
              :key="sublink.text"
              :active="isActiveLink(sublink)"
              class="sublink-item"
              @click="handleDrawerItemClick(sublink)"
            >
              <v-list-item-title class="drawer-title sublink-text">
                {{ sublink.text }}
              </v-list-item-title>
            </v-list-item>
          </v-list-group>
        </div>
      </v-list>
    </div>
  </v-navigation-drawer>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';

import useAppStore from '@/store/modules/pinia/app';
import useMfaStore from '@/store/modules/pinia/multi-factor-authentication';
import useAuth from '@/composables/useAuth';

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const { width } = useDisplay();

const appStore = useAppStore();
const mfaStore = useMfaStore();
const { canAccess } = useAuth();

const DRAWER_OPENED_GROUPS_STORAGE_KEY = 'iiot.drawer.openedGroups.v2';

const drawerRef = ref(null);
const isHoverExpanded = ref(false);
const isPointerInsideDrawer = ref(false);
const hasFocusInDrawer = ref(false);

const DRAWER_HIDDEN_BREAKPOINT = 600;
const DRAWER_COMPACT_BREAKPOINT = 990;
const isDrawerOverlayMode = computed(() => width.value <= DRAWER_HIDDEN_BREAKPOINT);
const isCompactDrawer = computed(() => width.value <= DRAWER_COMPACT_BREAKPOINT);
const isDesktopDrawerClosed = computed(() => !isDrawerOverlayMode.value && !appStore.drawer);
const isHoverOverlayDrawer = computed(() => isDesktopDrawerClosed.value && isHoverExpanded.value);
const isDrawerTemporary = computed(() => isDrawerOverlayMode.value);
const isDrawerPermanent = computed(() => !isDrawerTemporary.value);
const isRail = computed(() => !isDrawerOverlayMode.value && !appStore.drawer);
const isDrawerExpanded = computed(() => !isRail.value || isHoverOverlayDrawer.value);
const drawerModel = computed(() => (isDrawerOverlayMode.value ? appStore.drawer : true));

const links = computed(() => [
  {
    name: 'nodesMenu',
    icon: 'mdi-vector-line',
    text: t('drawer.links.nodes'),
    isExistsSubElement:
      canAccess('UI_NAV_NODES:VIEW') ||
      canAccess('UI_SUBNAV_NODE_UPDATE:VIEW') ||
      canAccess('UI_SUBNAV_NODE_UPDATE_LOG:VIEW'),
    isVisible:
      canAccess('UI_NAV_NODES:VIEW') ||
      canAccess('UI_SUBNAV_NODE_UPDATE:VIEW') ||
      canAccess('UI_SUBNAV_NODE_UPDATE_LOG:VIEW'),
    subLinks: [
      { to: '/nodes', text: t('drawer.links.view'), isVisible: canAccess('UI_NAV_NODES:VIEW') },
      { to: '/node/update/list', text: t('drawer.links.update'), isVisible: canAccess('UI_SUBNAV_NODE_UPDATE:VIEW') },
      {
        to: '/node/update/log',
        text: t('drawer.links.updateLog'),
        isVisible: canAccess('UI_SUBNAV_NODE_UPDATE_LOG:VIEW'),
      },
    ],
  },
  {
    icon: 'mdi-hexagon-multiple',
    to: '/workloads',
    src: 'WorkloadsDrawer.svg',
    srcSelected: 'WorkloadsSelectedDrawer.svg',
    text: t('drawer.links.workloads'),
    isVisible: canAccess('UI_NAV_WORKLOADS:VIEW'),
  },
  {
    name: 'deploy',
    icon: 'mdi-rocket-launch',
    text: t('drawer.links.deploy'),
    isExistsSubElement: canAccess('UI_NAV_DEPLOY:VIEW') || canAccess('UI_SUBNAV_DEPLOY_DRY_RUN:VIEW'),
    isVisible:
      canAccess('UI_NAV_DEPLOY:VIEW') ||
      canAccess('UI_SUBNAV_DEPLOY_LOG:VIEW') ||
      canAccess('UI_SUBNAV_DEPLOY_DRY_RUN:VIEW'),
    subLinks: [
      { to: '/deploy/workload', text: t('drawer.links.deployment'), isVisible: canAccess('UI_NAV_DEPLOY:VIEW') },
      { to: '/deploy/log', text: t('drawer.links.logs'), isVisible: canAccess('UI_SUBNAV_DEPLOY_LOG:VIEW') },
    ],
  },
  {
    icon: 'mdi-tag-text',
    to: '/labels',
    src: 'LabelsDrawer.svg',
    srcSelected: 'LabelsSelectedDrawer.svg',
    text: t('drawer.links.labels'),
    isVisible: canAccess('UI_NAV_LABELS:VIEW'),
  },
  {
    name: 'access',
    icon: 'mdi-account-multiple',
    text: t('drawer.links.access'),
    isExistsSubElement:
      canAccess('UI_NAV_USERS:VIEW') || canAccess('UI_NAV_ROLES:VIEW') || canAccess('UI_NAV_LDAP:VIEW'),
    isVisible: canAccess('UI_NAV_USERS:VIEW') || canAccess('UI_NAV_ROLES:VIEW') || canAccess('UI_NAV_LDAP:VIEW'),
    subLinks: [
      { to: '/users', text: t('drawer.links.users'), isVisible: canAccess('UI_NAV_USERS:VIEW') },
      { to: '/roles', text: t('drawer.links.roles'), isVisible: canAccess('UI_NAV_ROLES:VIEW') },
      { to: '/ldap', text: t('drawer.links.ldap'), isVisible: canAccess('UI_NAV_LDAP:VIEW') },
    ],
  },
  {
    to: '/remotes',
    icon: 'mdi-remote',
    text: t('drawer.links.remotes'),
    isVisible: canAccess('UI_NAV_REMOTE_CONNECTIONS:VIEW'),
  },
  {
    to: '/notifications',
    icon: 'mdi-bell',
    text: t('drawer.links.notifications'),
    isVisible: canAccess('UI_NAV_NOTIFICATION:VIEW'),
  },
  {
    to: '/registry',
    icon: 'mdi-docker',
    text: t('drawer.links.registry'),
    isVisible: canAccess('UI_REGISTRY:VIEW'),
  },
  {
    name: 'system',
    icon: 'mdi-clipboard-pulse-outline',
    text: t('drawer.links.system'),
    isExistsSubElement:
      canAccess('VERSION:LIST') ||
      canAccess('UI_USAGE_REPORT:VIEW') ||
      canAccess('UI_SERVER_AUDIT:VIEW') ||
      canAccess('UI_SERVER_LOGS:VIEW'),
    isVisible:
      canAccess('VERSION:LIST') ||
      canAccess('UI_USAGE_REPORT:VIEW') ||
      canAccess('UI_SERVER_AUDIT:VIEW') ||
      canAccess('UI_SERVER_LOGS:VIEW'),
    subLinks: [
      { to: '/available-updates', text: t('drawer.links.availableUpdates'), isVisible: canAccess('VERSION:LIST') },
      {
        to: '/usage-reports',
        text: t('drawer.links.usageReports'),
        isVisible: canAccess('UI_USAGE_REPORT:VIEW'),
      },
      {
        to: '/logs',
        text: t('drawer.links.logs'),
        isVisible: canAccess('UI_SERVER_AUDIT:VIEW') || canAccess('UI_SERVER_LOGS:VIEW'),
      },
      { to: '/about', text: t('drawer.links.about'), isVisible: true },
    ],
  },
]);

const getGroupKey = (link, index) => {
  if (link?.name) return link.name;
  if (!link?.subLinks?.length) return `group-${index}`;
  return `group-${index}`;
};

const getInitialOpenedGroups = () => {
  try {
    const rawOpenedGroups = localStorage.getItem(DRAWER_OPENED_GROUPS_STORAGE_KEY);
    const parsedOpenedGroups = rawOpenedGroups ? JSON.parse(rawOpenedGroups) : [];
    return Array.isArray(parsedOpenedGroups) ? parsedOpenedGroups : [];
  } catch {
    return [];
  }
};

const openedGroups = ref(getInitialOpenedGroups());

watch(
  openedGroups,
  (newOpenedGroups) => {
    localStorage.setItem(DRAWER_OPENED_GROUPS_STORAGE_KEY, JSON.stringify(newOpenedGroups));
  },
  { deep: true },
);

watch(
  links,
  (newLinks) => {
    const validGroupKeys = newLinks
      .filter((link) => link.subLinks && link.isVisible)
      .map((link, index) => getGroupKey(link, index));

    openedGroups.value = openedGroups.value.filter((groupKey) => validGroupKeys.includes(groupKey));
  },
  { immediate: true },
);

const generateId = (path) => (path ? path.replace(/^\//, '').replaceAll('/', '_') : 'nav-item');
const isActiveLink = (link) => {
  if (!link.to) return false;
  if (link.to === '/deploy/log' && route.path.startsWith('/deploy/details')) return true;
  return route.path.startsWith(link.to);
};

const isDrawerItemActive = (link, index) => {
  if (!link?.subLinks?.length) return isActiveLink(link);

  const hasActiveSubLink = link.subLinks.some((subLink) => isActiveLink(subLink));

  if (hasActiveSubLink) {
    const isGroupVisible =
      isDrawerExpanded.value && openedGroups.value.includes(getGroupKey(link, typeof index === 'number' ? index : -1));

    if (!isGroupVisible) return true;
  }

  return isActiveLink(link);
};

const collapseTemporaryExpandedDrawer = () => {
  if (!isDesktopDrawerClosed.value || !isHoverExpanded.value) return;

  isHoverExpanded.value = false;
  isPointerInsideDrawer.value = false;
  hasFocusInDrawer.value = false;
};

const handleDrawerItemClick = (link) => {
  if (link.to && route.path !== link.to) router.push(link.to);

  if (link.to) {
    if (!isPointerInsideDrawer.value) {
      collapseTemporaryExpandedDrawer();
    }

    if (isDrawerOverlayMode.value) {
      appStore.setDrawer(false);
    }
  }
};

const handleDrawerModelUpdate = (newValue) => {
  if (!isDrawerOverlayMode.value) return;
  appStore.setDrawer(!!newValue);
};

const closeHoverExpandedDrawer = () => {
  if (!isPointerInsideDrawer.value && !hasFocusInDrawer.value) {
    isHoverExpanded.value = false;
  }
};

const handleDrawerMouseEnter = () => {
  isPointerInsideDrawer.value = true;
  if (isDesktopDrawerClosed.value) {
    isHoverExpanded.value = true;
  }
};

const handleDrawerMouseLeave = () => {
  isPointerInsideDrawer.value = false;
  hasFocusInDrawer.value = false;
  isHoverExpanded.value = false;
};

const handleDrawerFocusIn = () => {
  hasFocusInDrawer.value = true;
  if (isDesktopDrawerClosed.value) {
    isHoverExpanded.value = true;
  }
};

const handleDrawerFocusOut = (event) => {
  const drawerElement = drawerRef.value?.$el;
  const nextFocusedElement = event.relatedTarget;

  hasFocusInDrawer.value = !!(drawerElement && nextFocusedElement && drawerElement.contains(nextFocusedElement));
  closeHoverExpandedDrawer();
};

watch(isDesktopDrawerClosed, (isClosed) => {
  if (!isClosed) {
    isHoverExpanded.value = false;
    isPointerInsideDrawer.value = false;
    hasFocusInDrawer.value = false;
  }
});

const isCompactDrawerInitialized = ref(false);

watch(
  isCompactDrawer,
  (isCompact) => {
    // Only apply auto-open/close logic after initial mount
    if (!isCompactDrawerInitialized.value) {
      isCompactDrawerInitialized.value = true;
      return;
    }

    appStore.setDrawer(!isCompact);
    isHoverExpanded.value = false;
    isPointerInsideDrawer.value = false;
    hasFocusInDrawer.value = false;
  },
  { immediate: true },
);

onMounted(async () => {
  try {
    await Promise.all([mfaStore.getMFAStatus()]);
  } catch (e) {
    console.debug('Drawer initialization error:', e);
  }
});
</script>

<style lang="scss" scoped>
#iiotAppDrawer {
  background: rgb(var(--v-theme-tertiary));
  border-right: none;
  transition: width 0.3s ease;
  margin-top: 1px;

  :deep(.v-navigation-drawer__content) {
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .side {
    flex: 1 1 auto;
    overflow-y: auto;
    overflow-x: hidden;
    padding-bottom: 20px;
    min-height: 0;
    &::-webkit-scrollbar {
      display: none;
    }
    scrollbar-width: none;

    .v-list {
      padding-bottom: 0;
    }
  }

  .drawer-item {
    min-height: 60px !important;
    color: rgb(var(--v-theme-secondary)) !important;
    padding-left: 4px !important;
    border-left: 3px solid transparent;

    :deep(.v-list-item__content) {
      overflow: visible !important;
    }

    &.v-list-item--active {
      background: rgba(var(--v-theme-primary), 0.1) !important;
      color: rgb(var(--v-theme-primary)) !important;
      border-left: 3px solid rgb(var(--v-theme-primary));
    }
  }

  .icon-container {
    width: 40px;
    display: flex;
    justify-content: center;
    margin-right: 6px;
  }

  .drawer-title {
    font-size: 19px !important;
    font-weight: 500;
    line-height: 1.4 !important;
    overflow: visible !important;
  }

  .sublink-item {
    padding-left: 50px !important;
    min-height: 48px;
    color: rgb(var(--v-theme-secondary)) !important;
    border-left: 3px solid transparent;

    :deep(.v-list-item__content) {
      overflow: visible !important;
    }

    .sublink-text {
      font-size: 17px !important;
      line-height: 1.4 !important;
      overflow: visible !important;
    }

    &.v-list-item--active {
      background: rgba(var(--v-theme-primary), 0.1) !important;
      color: rgb(var(--v-theme-primary)) !important;
      border-left: 3px solid rgb(var(--v-theme-primary));

      .sublink-text {
        color: rgb(var(--v-theme-primary)) !important;
      }
    }
  }

  &.hover-overlay-open {
    width: 240px !important;
    position: absolute !important;
    z-index: 11;
  }

  &.v-navigation-drawer--rail:not(.hover-overlay-open) {
    .drawer-item {
      padding: 0 !important;
      justify-content: center !important;
      :deep(.v-list-item__prepend) {
        margin: 0 !important;
        width: 100% !important;
        justify-content: center !important;
      }
    }
    .icon-container {
      margin: 0 !important;
      width: 100%;
    }
    :deep(.v-list-item-title),
    :deep(.v-list-item__append),
    :deep(.v-list-group__items) {
      display: none !important;
    }
  }
}

.group-arrow-btn {
  transition: transform 0.2s;

  :deep(.v-icon) {
    font-size: 16px !important;
  }
}

.drawer-item-padding {
  padding-left: 13px !important;
}
</style>
